import {
    VoucherNursingCarePlanUpdateInput,
} from './../../../../types/src/services/input-type/voucher/voucher-nursing-care-plan.update-input';
import {
    VoucherNursingCarePlanGetInput,
} from '../../../../types/src/services/input-type/voucher/voucher-nursing-care-plan.get-input';
import {
    VoucherNursingCareAssessmentCreateInput,
} from '@libs/types/src/services/input-type/voucher/voucher-nursing-care-assessment.create-input';
import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    VoucherNursingCareAssessmentDeleteInput,
    VoucherNursingCareAssessmentGridItemObject,
    VoucherNursingCareAssessmentGridObject,
    VoucherNursingCareAssessmentUpdateInput,
    VoucherNursingCareDiagnosisCreateInput,
    VoucherNursingCareDiagnosisDeleteInput,
    VoucherNursingCareDiagnosisGridObject,
    VoucherNursingCareDiagnosisUpdateInput,
} from '@it2go/types';
import {
    voucherNursingCareAssessmentFrag,
    voucherNursingCareDiagnosisCodeFrag,
    voucherNursingCareDiagnosisFrag,
    voucherNursingCarePlanFrag,
} from './types/voucher.types';
import { filterFrag } from './types/filter.types';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    VoucherNursingCareExportInput,
} from '@libs/types/src/services/input-type/voucher/voucher-nursing-care.export-input';
import { exportFrag } from './types/common.types';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import {
    VoucherNursingCareEntity,
} from '../../../../services/src/domain/entity/voucher/nursing-care/voucher-nursing-care.entity';

@Injectable({ providedIn: 'root' })
export class NursingCareGqlServiceV2 extends GqlServiceV2 {
    getDiagnosisCodes(input: GridInput | null, frag: string = voucherNursingCareDiagnosisCodeFrag) {
        return this.query<VoucherNursingCareDiagnosisGridObject>(
            `
                query($input: GridInput) {
                    voucherNursingCare {
                        filterDiagnosisCodes(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getNursingCarePlan(input: VoucherNursingCarePlanGetInput, frag: string = voucherNursingCarePlanFrag) {
        return this.query<VoucherNursingCareEntity>(
            `
                query($input: VoucherNursingCarePlanGetInput!) {
                    voucherNursingCare {
                        getPlan(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateNursingCarePlan(input: VoucherNursingCarePlanUpdateInput, frag: string = voucherNursingCarePlanFrag) {
        return this.mutate(
            `
                mutation($input: VoucherNursingCarePlanUpdateInput!) {
                    voucherNursingCare {
                        updatePlan(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getNursingCareAssessments(input: GridInput | null, frag: string = voucherNursingCareAssessmentFrag) {
        return this.query<VoucherNursingCareAssessmentGridObject>(
            `
                query($input: GridInput) {
                    voucherNursingCare {
                        filterAssessments(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getNursingCareDiagnoses(input: GridInput | null, frag: string = voucherNursingCareDiagnosisFrag) {
        return this.query<VoucherNursingCareDiagnosisGridObject>(
            `
                query($input: GridInput) {
                    voucherNursingCare {
                        filterDiagnoses(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createNursingCareAssessment(input: VoucherNursingCareAssessmentCreateInput, frag: string = 'ok') {
        return this.mutate<VoucherNursingCareAssessmentGridItemObject>(
            `
                mutation($input: VoucherNursingCareAssessmentCreateInput!) {
                    voucherNursingCare {
                        createAssessment(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    createNursingCareDiagnosis(input: VoucherNursingCareDiagnosisCreateInput, frag: string = 'ok') {
        return this.mutate<VoucherNursingCareDiagnosisGridObject>(
            `
                mutation($input: VoucherNursingCareDiagnosisCreateInput!) {
                    voucherNursingCare {
                        createDiagnosis(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteNursingCareAssessment(input: VoucherNursingCareAssessmentDeleteInput, frag: string = 'ok') {
        return this.mutate(
            `
                mutation($input: VoucherNursingCareAssessmentDeleteInput!) {
                    voucherNursingCare {
                        deleteAssessment(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteNursingCareDiagnosis(input: VoucherNursingCareDiagnosisDeleteInput, frag: string = 'ok') {
        return this.mutate(
            `
                mutation($input: VoucherNursingCareDiagnosisDeleteInput!) {
                    voucherNursingCare {
                        deleteDiagnosis(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateNursingCareAssessment(input: VoucherNursingCareAssessmentUpdateInput, frag: string = voucherNursingCareAssessmentFrag) {
        return this.mutate(
            `
                mutation($input: VoucherNursingCareAssessmentUpdateInput!) {
                    voucherNursingCare {
                        updateAssessment(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateNursingCareDiagnosis(input: VoucherNursingCareDiagnosisUpdateInput, frag: string = 'ok') {
        return this.mutate(
            `
                mutation($input: VoucherNursingCareDiagnosisUpdateInput!) {
                    voucherNursingCare {
                        updateDiagnosis(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    exportNursingCare(input: VoucherNursingCareExportInput, frag: string = exportFrag) {
        return this.query<FolderPrintObject>(
            `
                query($input: VoucherNursingCareExportInput!) {
                    voucherNursingCare {
                        export(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    exportNursingCareAssessment(input: VoucherNursingCareExportInput, frag: string = exportFrag) {
        return this.query<FolderPrintObject>(
            `
                query($input: VoucherNursingCareExportInput!) {
                    voucherNursingCare {
                        exportAssessment(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
